import { produce } from "immer";
import { type SetOptional } from "type-fest";

import { normalizeBase } from "@ds/model/helpers";

import {
  isApiCampaignItem,
  isApiCampaignItemArray,
  isApiCampaignItemQueryOutput,
  isCampaignItemArray,
  isCampaignItemQueryOutput,
} from "./model";

const normalizer = (entity: ApiCampaignItem): ApiCampaignItem =>
  produce(normalizeBase(entity), (draft: ApiCampaignItem) => {
    draft.weight = entity.weight || undefined;
  });

export const normalize = ((
  entityOrEntities: ApiCampaignItem | ApiCampaignItem[] | QueryOutput<ApiCampaignItem>,
): ApiCampaignItem | ApiCampaignItem[] | QueryOutput<ApiCampaignItem> => {
  if (isApiCampaignItemQueryOutput(entityOrEntities)) {
    return produce(entityOrEntities, draft => {
      draft.items = entityOrEntities.items.map(normalizer);
    });
  }

  return isApiCampaignItemArray(entityOrEntities) ? entityOrEntities.map(normalizer) : normalizer(entityOrEntities);
}) as {
  (campaignItem: ApiCampaignItem): ApiCampaignItem;
  (campaignItems: ApiCampaignItem[]): ApiCampaignItem[];
  (campaignItemsQueryOutput: QueryOutput<ApiCampaignItem>): QueryOutput<ApiCampaignItem>;
};

const apiToModelNormalizer = (entity: CampaignItem | ApiCampaignItem): CampaignItem => {
  if (!isApiCampaignItem(entity)) {
    return entity;
  }

  return produce(normalizeBase(entity), (draft: SetOptional<ApiCampaignItem, "playlist">) => {
    (draft as CampaignItem).playlist_name = entity.playlist?.name;
    if (entity.playlist !== null && entity.playlist !== undefined) {
      (draft as CampaignItem).playlist_items_count = entity.playlist.playlist_items_count;
    }
    delete draft.playlist;
  });
};

export const apiToModelNormalize = ((
  entityOrEntities:
    | CampaignItem
    | CampaignItem[]
    | QueryOutput<CampaignItem>
    | ApiCampaignItem
    | ApiCampaignItem[]
    | QueryOutput<ApiCampaignItem>,
): CampaignItem | CampaignItem[] | QueryOutput<CampaignItem> => {
  if (isCampaignItemQueryOutput(entityOrEntities)) {
    return produce(entityOrEntities, draft => {
      draft.items = entityOrEntities.items.map(item => apiToModelNormalizer(item) as ApiCampaignItem);
    });
  }

  return isCampaignItemArray(entityOrEntities)
    ? entityOrEntities.map(apiToModelNormalizer)
    : apiToModelNormalizer(entityOrEntities);
}) as {
  (campaignItem: CampaignItem): CampaignItem;
  (campaignItem: CampaignItem[]): CampaignItem[];
  (campaignItem: QueryOutput<ApiCampaignItem>): QueryOutput<CampaignItem>;
  (campaignItem: ApiCampaignItem): CampaignItem;
  (campaignItems: ApiCampaignItem[]): CampaignItem[];
  (campaignItemsQueryOutput: QueryOutput<ApiCampaignItem>): QueryOutput<CampaignItem>;
};
